import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Form, Input, Modal, Segmented, Space, Typography } from 'antd';
import { COMPONENT_CONSTRAINTS } from '../constants';
import { COMPONENT_TEMPLATES } from '../blocks/templates';

interface FormValues {
  name: string;
  category?: string;
}

/** K-37 — "Boş / Şablondan" seçimi; şablon seçilirse componentJson tohumlanır. */
export interface ComponentCreateResult {
  name: string;
  category?: string;
  templateId?: string;
}

interface ComponentCreateModalProps {
  open: boolean;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: ComponentCreateResult) => void;
}

type Mode = 'blank' | 'template';

/** "Yeni Bileşen" — ad/kategori + boş/şablon seçimi; şema editörde doldurulur. */
export function ComponentCreateModal({ open, saving, onCancel, onCreate }: ComponentCreateModalProps) {
  const { t } = useTranslation(['component-studio', 'common']);
  const tr = t as unknown as (key: string) => string;
  const [form] = Form.useForm<FormValues>();
  const [mode, setMode] = useState<Mode>('blank');
  const [templateId, setTemplateId] = useState<string>(COMPONENT_TEMPLATES[0]?.id ?? '');

  // Açılışta form alanlarını sıfırla (setState DEĞİL → set-state-in-effect uyarısı yok).
  useEffect(() => {
    if (open) form.setFieldsValue({ name: '', category: '' });
  }, [open, form]);

  // Açılış geçişinde mod/şablon seçimini render-fazında sıfırla (React "önceki render"
  // deseni; effect değil → cascading render yok, afterOpenChange yarışı yok).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setMode('blank');
      setTemplateId(COMPONENT_TEMPLATES[0]?.id ?? '');
    }
  }

  const submit = () => {
    void (async () => {
      try {
        const v = await form.validateFields();
        onCreate({
          name: v.name.trim(),
          category: v.category?.trim() || undefined,
          templateId: mode === 'template' ? templateId : undefined,
        });
      } catch {
        // inline validation
      }
    })();
  };

  return (
    <Modal
      open={open}
      title={t('create.title')}
      onCancel={onCancel}
      onOk={submit}
      okText={t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
      width={mode === 'template' ? 640 : 520}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item
          name="name"
          label={t('create.nameField')}
          rules={[{ required: true, message: t('create.nameRequired') }]}
        >
          <Input
            maxLength={COMPONENT_CONSTRAINTS.maxNameLength}
            placeholder={t('create.namePlaceholder')}
            data-testid="component-create-name"
          />
        </Form.Item>

        <Form.Item name="category" label={t('create.categoryField')} help={t('create.categoryHint')}>
          <Input
            maxLength={COMPONENT_CONSTRAINTS.maxCategoryLength}
            placeholder={t('create.categoryPlaceholder')}
            data-testid="component-create-category"
          />
        </Form.Item>

        <Form.Item label={t('create.startFrom')} style={{ marginBottom: mode === 'template' ? 12 : 0 }}>
          <Segmented<Mode>
            value={mode}
            onChange={setMode}
            options={[
              { value: 'blank', label: t('create.blank') },
              { value: 'template', label: t('create.fromTemplate') },
            ]}
            data-testid="component-create-mode"
          />
        </Form.Item>
      </Form>

      {mode === 'template' ? (
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}
          data-testid="component-template-gallery"
        >
          {COMPONENT_TEMPLATES.map((tpl) => {
            const selected = templateId === tpl.id;
            return (
              <Card
                key={tpl.id}
                size="small"
                hoverable
                onClick={() => setTemplateId(tpl.id)}
                style={{
                  cursor: 'pointer',
                  borderColor: selected ? 'var(--ds-brand, #0a7d55)' : undefined,
                  borderWidth: selected ? 2 : 1,
                }}
                data-testid={`component-template:${tpl.id}`}
              >
                <Space direction="vertical" size={2}>
                  <Typography.Text strong>{tr(`templates.${tpl.id}.name`)}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {tr(`templates.${tpl.id}.description`)}
                  </Typography.Text>
                </Space>
              </Card>
            );
          })}
        </div>
      ) : null}
    </Modal>
  );
}
